/**
 * AddBuildingDialog — Tracker-side add building flow.
 *
 * Step 1 (lookup): address autocomplete -> BIN/BBL via `checkEligibility()`.
 * The BIN tab fast-paths into Step 2 with a synthetic `TrackingResult` (no
 * PLUTO prefill). The CSV tab is single-step bulk-add and skips Step 2.
 *
 * Step 2 (confirm): editable GFA / primary use / year built, pre-filled from
 * PLUTO, and three CTAs.
 *   - "Confirm and add" POSTs /api/tracking/buildings/ with the typed
 *     confirmed_* fields; BE stamps each ``tracker_owner_attested``.
 *   - "Add with City estimates" POSTs nyc_bin + confirmed_bbl only; BE sets
 *     ``source_provenance={}`` and the dashboard shows "Confirm details to
 *     estimate", which re-opens this dialog.
 *   - "I'll share ESPM instead →". /portal/espm-share is NOT in routes/portal/,
 *     so it logs a TODO and closes rather than dead-ending.
 *
 * GUARDRAILS. Every field is an EDITABLE input, not a button: click-through
 * carries 85-95% confirmation bias (Nielsen Norman); typed re-entry does not.
 * Copy is counsel-honest — "Confirm" not "Attest", "City's tax assessment" not
 * "verified by NYC". The PLUTO badge uses FileCheck, NEVER ShieldCheck, which
 * is filing-app reserved. Only the primary CTA autoFocuses: the skip path must
 * not be the default action (WCAG 2.4.3). CTAs stack below `sm` for 375px.
 */
import React, { useState, useCallback, useEffect, useId, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/shared/ui/dialog";
import { Button } from "@/components/shared/ui/button";
import { Input } from "@/components/shared/ui/input";
import { Label } from "@/components/shared/ui/label";
import { cn } from "@/lib/utils";
import { createBuilding } from "@/api/building";
import {
  checkEligibility,
  createTrackingBuilding,
  locateDetail,
  resolveCanonicalPrimaryFunction,
  PRIMARY_FUNCTION_OPTIONS,
  type TrackingResult,
  type CreateTrackingBuildingPayload,
  type LocateCandidate,
  type LocateDetailResponse,
} from "@/api/tracking";
import { parseTrackingFile } from "@/utils/parseTrackingFile";
import { toast } from "@/queries/useToast";
import AddBuildingLocatePanel from "./AddBuildingLocatePanel";
import AddBuildingVerifyPanel from "./AddBuildingVerifyPanel";
import CollectingFrame from "./CollectingFrame";
import AddBuildingLl87DiligencePanel from "./AddBuildingLl87DiligencePanel";
// The canonical source-label mapping lives in DataLineageRow — one source of
// truth across chat tool-cards, Tracker building detail and this dialog. This
// copy does not surface cascade-source labels yet (the badge is hard-coded
// "PLUTO est."); the import stays wired so the forthcoming provenance lines do
// not re-introduce a duplicate table. DataLineageRow.test.tsx pins the labels
// and a SOURCE_LABELS uniqueness case forbids re-declaring it. Do NOT inline.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  SOURCE_LABELS,
  labelForSource,
  DIVERGENCE_THRESHOLD_PCT,
} from "@/components/portal/DataLineageRow";
import {
  Upload,
  Loader2,
  Plus,
  CheckCircle2,
  XCircle,
  Search,
  ArrowLeft,
} from "lucide-react";

/** Prefill payload — the dashboard's "Confirm details to estimate" CTA opens
 * the dialog straight on Step 2 for that building. Every identity field is
 * optional; each populated one pre-fills its Step-2 input. */
export interface AddBuildingPrefill {
  bin: string;
  bbl?: string | null;
  addressResolved?: string | null;
  grossSqft?: number | null;
  yearBuilt?: number | null;
  primaryFunction?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingBins?: Set<string>;
  /** When provided, opens directly on Step 2 with these values. */
  prefill?: AddBuildingPrefill;
}

type UploadStatus = "idle" | "parsing" | "creating" | "done" | "error";
// "collecting" is the post-POST warmup step (CollectingFrame), rendered AFTER
// the POST returns the building UUID — the first moment there is something to
// warmup-poll. The brief names the machine `locate → collecting → verify →
// confirm`; our "confirm" step IS the Verify panel.
//
// "diligence" is the FR-26 LL87 surface after warmup: it reads the DB-first
// `codes.ll87` the warmup just computed and shows the LL87 due-year / filing
// diligence (buyers inherit five-figure accruals). Warmup is the one permitted
// live-call path (§7); this step is DB-first read-only.
type Step = "locate" | "confirm" | "collecting" | "diligence" | "csv";

const AddBuildingDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  existingBins = new Set(),
  prefill,
}) => {
  const [step, setStep] = useState<Step>("locate");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadMessage, setUploadMessage] = useState("");
  // UUID from the createTrackingBuilding POST. CollectingFrame needs one to
  // poll warmup-status. `null` until the POST succeeds.
  const [createdBuildingId, setCreatedBuildingId] = useState<string | null>(null);

  // Step 2 (confirm) state — resolved building + editable typed values.
  const [resolved, setResolved] = useState<TrackingResult | null>(null);
  const [gfaInput, setGfaInput] = useState("");
  const [primaryFunctionInput, setPrimaryFunctionInput] = useState("");
  const [yearBuiltInput, setYearBuiltInput] = useState("");
  // Multi-source per-field map from POST /api/tracking/locate-detail/, fetched
  // in parallel with checkEligibility. Drives the Verify panel's provenance
  // pills and confidence bar. ``null`` while loading or on failure; the panel
  // falls back to single-PLUTO labels.
  const [detail, setDetail] = useState<LocateDetailResponse | null>(null);
  // Race guard. Clicking candidate A then B before A's Promise.all settles
  // would let A's late response clobber B's state and POST confirmed_bbl for
  // the wrong building. Mirrors LocatePanel's latestQueryRef.
  const latestPickRef = useRef<string | null>(null);
  // Identity fields overridden this session. Such a field drops the
  // server-resolved PLUTO/LL84 pills for "Just confirmed by you" and counts as
  // high in the confidence tier, so the bar cannot claim "all sources agree" on
  // a value the user just overrode.
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  // Stable aria-describedby ids; useId is hook-stable across renders.
  const gfaHelpId = useId();
  const fnHelpId = useId();
  const yrHelpId = useId();

  const queryClient = useQueryClient();

  const resetState = useCallback(() => {
    setStep("locate");
    setUploadStatus("idle");
    setUploadMessage("");
    setIsSubmitting(false);
    setResolved(null);
    setGfaInput("");
    setPrimaryFunctionInput("");
    setYearBuiltInput("");
    setDetail(null);
    latestPickRef.current = null;
    setEditedFields(new Set());
    setCreatedBuildingId(null);
  }, []);

  // The dashboard passes a prefill when the user clicks "Confirm details to
  // estimate" on a row: open straight on Step 2 with the resolved BIN/BBL/GFA.
  useEffect(() => {
    if (open && prefill) {
      const synthetic: TrackingResult = {
        addressInput: prefill.addressResolved ?? null,
        binInput: prefill.bin,
        addressResolved: prefill.addressResolved ?? null,
        borough: null,
        bbl: prefill.bbl ?? null,
        bin: prefill.bin,
        grossSqft: prefill.grossSqft ?? null,
        numBuildings: null,
        codes: {},
        error: null,
      };
      setResolved(synthetic);
      setGfaInput(prefill.grossSqft != null ? String(prefill.grossSqft) : "");
      setPrimaryFunctionInput(prefill.primaryFunction ?? "");
      setYearBuiltInput(prefill.yearBuilt != null ? String(prefill.yearBuilt) : "");
      setStep("confirm");
    }
  }, [open, prefill]);

  const handleClose = (val: boolean) => {
    if (!val) resetState();
    onOpenChange(val);
  };

  /**
   * Step 1 → Step 2. Hits check-eligibility with the picked BIN to pull the
   * PLUTO snapshot (GFA / year_built / primary_function) so Step 2's inputs
   * land pre-filled. EVERY advance goes through this path.
   */
  const handleCandidatePick = useCallback(
    async (c: LocateCandidate) => {
      if (!c.bin) {
        toast({
          variant: "destructive",
          description: "Match has no BIN — pick another or enter manually.",
        });
        return;
      }
      if (existingBins.has(c.bin)) {
        toast({
          description: `"${
            c.addressResolved || c.bin
          }" is already in your buildings.`,
        });
        handleClose(false);
        return;
      }
      setIsSubmitting(true);
      latestPickRef.current = c.bin;
      // A fresh candidate starts with no overrides; otherwise the previous
      // building's "JUST CONFIRMED BY YOU" pill would lie about this one.
      setEditedFields(new Set());
      try {
        // In parallel: checkEligibility seeds the editable inputs with PLUTO
        // values; locateDetail returns the per-field multi-source map
        // (PLUTO + LL84 + CBL) for the pills and confidence bar. The detail
        // call is best-effort — a failure leaves the single-PLUTO fallback.
        const [result, detailRes] = await Promise.all([
          checkEligibility([{ bin: c.bin }]),
          locateDetail(c.bin).catch((e) => {
            // Log so QA can diagnose the single-PLUTO fallback without
            // DevTools-network.
            // eslint-disable-next-line no-console
            console.warn("[AddBuildingDialog] locate-detail failed:", e);
            return null;
          }),
        ]);
        // Drop a late response from a superseded candidate: it would write
        // stale identity into the Verify panel and POST the wrong
        // confirmed_bbl on submit.
        if (latestPickRef.current !== c.bin) return;
        const first = result.results?.[0];
        const synthetic: TrackingResult = first ?? {
          addressInput: null,
          binInput: c.bin,
          addressResolved: c.addressResolved ?? null,
          borough: c.borough ?? null,
          bbl: c.bbl ?? null,
          bin: c.bin,
          grossSqft: null,
          numBuildings: null,
          codes: {},
          error: null,
        };
        setResolved(synthetic);
        setGfaInput(
          synthetic.grossSqft != null ? String(synthetic.grossSqft) : "",
        );
        // check-eligibility now returns year_built + primary_function alongside
        // grossSqft. Read them via Record<string, unknown> rather than widening
        // TrackingResult, which other consumers share.
        const extras = first as unknown as Record<string, unknown>;
        const yb = extras?.yearBuilt ?? extras?.year_built;
        const pf = extras?.primaryFunction ?? extras?.primary_function;
        setYearBuiltInput(
          typeof yb === "number" || typeof yb === "string" ? String(yb) : "",
        );
        // Prefer locate-detail over checkEligibility's scalar: it carries BOTH
        // PLUTO (BldgClass code) and LL84 (PropType label), so the dropdown
        // gets the canonical ESPM vocabulary (LL84 when present, PLUTO
        // letter-translated otherwise) instead of a 2-char DOF code like "O6".
        const canonicalPf = resolveCanonicalPrimaryFunction(detailRes);
        setPrimaryFunctionInput(
          canonicalPf || (typeof pf === "string" ? pf : ""),
        );
        setDetail(detailRes);
        setStep("confirm");
      } catch (e: unknown) {
        if (latestPickRef.current !== c.bin) return;
        const err = e as { response?: { data?: Record<string, unknown> } };
        const errDetail =
          (err?.response?.data as { error?: string })?.error ||
          "Failed to load building details";
        toast({ variant: "destructive", description: errDetail });
      } finally {
        if (latestPickRef.current === c.bin) setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existingBins],
  );

  /** Step 2 — POST /api/tracking/buildings/.
   *
   * @param confirmed - true includes the user-typed confirmed_* fields; false
   *                    posts nyc_bin + confirmed_bbl only, so the BE sets
   *                    source_provenance={} and the dashboard shows the
   *                    "Confirm details" CTA.
   */
  const handleSubmitConfirm = async (confirmed: boolean) => {
    if (!resolved?.bin) return;
    setIsSubmitting(true);
    try {
      const payload: CreateTrackingBuildingPayload = {
        nyc_bin: resolved.bin,
        // Required by the BE serializer. Accepting the resolved building IS
        // confirming the BBL; this is not the "City estimate" axis. May be ""
        // on the typed-BIN flow, which the BE rejects.
        confirmed_bbl: resolved.bbl ?? "",
        // The picker's selected label, so the header and portfolio row match
        // the address clicked. Without it the BE wrapper sends "New Building",
        // the inner serializer falls back to the resolver's first ranked
        // feature, and the persisted name can be a different alt-address on the
        // same BBL ("1088 FLATBUSH AVENUE" picked, "2112 CORTELYOU ROAD" shown).
        ...(resolved.addressResolved
          ? { name: resolved.addressResolved }
          : {}),
      };

      if (confirmed) {
        const gfaNum = gfaInput.trim() ? Number(gfaInput) : NaN;
        if (Number.isFinite(gfaNum) && gfaNum > 0) {
          payload.confirmed_gfa = gfaNum;
        }
        const fn = primaryFunctionInput.trim();
        if (fn) payload.confirmed_primary_function = fn;
        const yrNum = yearBuiltInput.trim() ? Number(yearBuiltInput) : NaN;
        if (Number.isFinite(yrNum) && yrNum > 0) {
          payload.confirmed_year_built = yrNum;
        }
      }

      // Fall back to the legacy /buildings/ create when there is no BBL:
      // pre-3.C BE deploys reject the tracking endpoint without one, and the
      // address tab can resolve a BIN with no BBL.
      let newId: string | null = null;
      if (!payload.confirmed_bbl && !confirmed) {
        const created = await createBuilding({
          name: resolved.addressResolved || resolved.bin,
          nyc_bin: resolved.bin,
        });
        newId =
          (created && typeof (created as { id?: unknown }).id === "string"
            ? (created as { id: string }).id
            : null) ?? null;
      } else {
        const created = await createTrackingBuilding(payload);
        newId = typeof created?.id === "string" ? created.id : null;
      }

      queryClient.invalidateQueries({ queryKey: ["portal"] });
      toast({
        description: confirmed
          ? `Building added with your confirmed details.`
          : `Building added with City estimates. Confirm details when you're ready.`,
      });

      // Flip to the Collecting frame for warmup. No id (legacy /buildings/ path
      // on some shapes) means nothing to poll, so close instead.
      if (newId) {
        setCreatedBuildingId(newId);
        setStep("collecting");
      } else {
        handleClose(false);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, unknown> } };
      const data = err?.response?.data;
      const detail =
        (data as { nyc_bin?: string[] })?.nyc_bin?.[0] ||
        (data as { confirmed_bbl?: string[] })?.confirmed_bbl?.[0] ||
        (data as { error?: string })?.error ||
        (data as { detail?: string })?.detail ||
        "Failed to add building";
      toast({
        variant: detail.includes("already exists") ? "default" : "destructive",
        description: detail,
      });
      if (detail.includes("already exists")) handleClose(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Tertiary CTA. /portal/espm-share is not in routes/portal/ yet, so log a
   * TODO and close rather than navigate to a 404. */
  const handleEspmShare = () => {
    // TODO(phase-3.D): wire to /portal/espm-share once the route lands. Close
    // cleanly, and emit a console marker so QA can confirm the click fired.
    // eslint-disable-next-line no-console
    console.info(
      "[AddBuildingDialog] ESPM share flow not yet routed — closing dialog. " +
        "TODO(phase-3.D): navigate to /portal/espm-share.",
    );
    toast({
      description: "ESPM share is coming soon. We'll email you a share link.",
    });
    handleClose(false);
  };

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      setUploadStatus("parsing");
      setUploadMessage("Parsing file...");
      try {
        const buildings = await parseTrackingFile(file);
        if (buildings.length === 0) {
          setUploadStatus("error");
          setUploadMessage(
            "No buildings found. Ensure the file has Address or NYC_BIN columns.",
          );
          return;
        }

        setUploadStatus("creating");
        setUploadMessage(`Processing ${buildings.length} building(s)...`);

        let successCount = 0;
        let duplicateCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        const addedBins = new Set(existingBins);

        for (const b of buildings) {
          const label = b.address || b.bin || "Unknown";
          let bin = b.bin?.trim() || "";
          let name = b.address || bin || "Unknown";

          if (!bin && b.address) {
            try {
              const r = await checkEligibility([{ address: b.address }]);
              const first = r.results?.[0];
              if (first?.bin) {
                bin = first.bin;
                name = first.addressResolved || b.address;
              }
            } catch {
              // resolution failed — caught as empty BIN below
            }
          }

          if (!bin) {
            errorCount++;
            if (errors.length < 5)
              errors.push(`${label}: could not resolve BIN`);
            continue;
          }

          if (addedBins.has(bin)) {
            duplicateCount++;
            continue;
          }

          try {
            await createBuilding({ name, nyc_bin: bin });
            addedBins.add(bin);
            successCount++;
          } catch (err: unknown) {
            const e = err as {
              response?: { data?: Record<string, unknown> };
              message?: string;
            };
            const detail =
              (e?.response?.data as { nyc_bin?: string[] })?.nyc_bin?.[0] ||
              (e?.response?.data as { error?: string })?.error ||
              (e?.response?.data as { detail?: string })?.detail ||
              e?.message ||
              "Unknown error";
            if (detail.includes("already exists")) {
              addedBins.add(bin);
              duplicateCount++;
            } else {
              errorCount++;
              if (errors.length < 5) errors.push(`${label}: ${detail}`);
            }
          }
        }

        queryClient.invalidateQueries({ queryKey: ["portal"] });
        setUploadStatus(
          errorCount > 0 && successCount === 0 ? "error" : "done",
        );
        const parts: string[] = [];
        if (successCount > 0) parts.push(`${successCount} added`);
        if (duplicateCount > 0)
          parts.push(`${duplicateCount} already in your buildings`);
        if (errorCount > 0) parts.push(`${errorCount} failed`);
        const summary = parts.join(", ");
        setUploadMessage(
          errors.length > 0
            ? `${summary}.\n${errors.join("\n")}${errorCount > 5 ? `\n…and ${errorCount - 5} more` : ""}`
            : `Done! ${summary}.`,
        );

        if (successCount > 0) {
          toast({
            description: `${successCount} building(s) added from file.`,
          });
        }
      } catch (e: unknown) {
        const err = e as { message?: string };
        setUploadStatus("error");
        setUploadMessage(err.message || "Failed to parse file");
      }
    },
    [queryClient, existingBins],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
    disabled: uploadStatus === "creating",
  });

  // Tertiary CTA copy: "share", not "verify" — the action is routing, not
  // attestation.

  /** Per-field provenance pills from the locate-detail map, falling back to a
   * single PLUTO pill in flight or where the field had no source coverage. The
   * fallback keeps the BE response's shape, so the Verify panel needs no
   * "loading" branch. A field the user overrode renders one "Just confirmed by
   * you" pill instead, so the UI never claims PLUTO+LL84 agreement on a value
   * neither source said.
   */
  const sourcesFor = (
    fieldKey: string,
    fallbackLabel: string,
  ): { label: string; isOwnerAttested?: boolean }[] => {
    if (editedFields.has(fieldKey)) {
      return [{ label: "Just confirmed by you", isOwnerAttested: true }];
    }
    const values = detail?.fieldSources?.[fieldKey]?.values ?? [];
    if (values.length === 0) {
      return [{ label: fallbackLabel, isOwnerAttested: false }];
    }
    return values.map((v) => ({
      label: v.label,
      // Owner-attested provenance only appears AFTER the user confirms; at Add
      // Building time every server-side source is a city authority.
      isOwnerAttested: false,
    }));
  };
  const conflictFor = (fieldKey: string): boolean => {
    // A user override resolves any prior conflict — nothing left for the
    // cross-source dashed-warn pill to surface.
    if (editedFields.has(fieldKey)) return false;
    return detail?.fieldSources?.[fieldKey]?.hasConflict ?? false;
  };

  // RE-DERIVED every render, not read from ``detail.confidence``, so user edits
  // flip the bar live. An overridden field counts as high-confidence.
  //   "high"     — every required field is multi-source agreed OR overridden
  //   "review"   — any non-overridden field has a conflict flag
  //   "estimate" — fallthrough (missing / single-source / non-attested)
  //
  // ``postal_code`` is excluded from the "≥2 sources OR an override" check:
  // it is structurally single-source, CBL being the only NYC dataset with a
  // building-level zip we can join on. Mirrors ``_compute_confidence``.
  const requiredKeys: readonly string[] = [
    "grossFloorArea",
    "primaryFunction",
    "yearBuilt",
  ];
  let anyConflictUnedited = false;
  let allEffectivelyHigh = true;
  let anyMissingUnedited = false;
  for (const k of requiredKeys) {
    if (editedFields.has(k)) continue; // counts as high
    const fs = detail?.fieldSources?.[k];
    const vals = fs?.values ?? [];
    if (vals.length === 0) {
      anyMissingUnedited = true;
      allEffectivelyHigh = false;
      continue;
    }
    if (fs?.hasConflict) {
      anyConflictUnedited = true;
      allEffectivelyHigh = false;
      continue;
    }
    if (vals.length < 2) allEffectivelyHigh = false;
  }
  const uiConfidence: "high" | "review" | "estimate" = anyConflictUnedited
    ? "review"
    : allEffectivelyHigh
      ? "high"
      : "estimate";
  // Keys off the server's "missing" tier (no data on any source), but a user
  // who filled every field via the inline pencils has MADE the data exist, so
  // an all-overridden state lets attest proceed.
  const allRequiredEdited = requiredKeys.every((k) => editedFields.has(k));
  const cannotAttest =
    detail?.confidence === "missing" && !allRequiredEdited && anyMissingUnedited;

  const conflictCount = detail
    ? Object.entries(detail.fieldSources ?? {}).filter(
        ([k, f]) => f.hasConflict && !editedFields.has(k),
      ).length
    : 0;

  // Modal width per the Claude Design source: 680px for Confirm (VerifyPanel /
  // A3_VerifyClean), 560px for Locate, Collecting, Diligence and CSV.
  // Tailwind's default `max-w-md` is 448px, 112px narrower than the design.
  const widthClass =
    step === "confirm" ? "sm:max-w-[680px]" : "sm:max-w-[560px]";

  // Auto-advance hands off to the FR-26 LL87 diligence surface (which reads the
  // DB-first `codes.ll87` the warmup just computed) rather than closing.
  const handleCollectingAdvance = useCallback(() => {
    // Invalidate for the snapshot counts that landed during Collecting; the new
    // row then appears in its "syncing" treatment on the page underneath.
    queryClient.invalidateQueries({ queryKey: ["portal"] });
    setStep("diligence");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  // Diligence dismissed — invalidate the codes cache so the building page
  // reflects the same LL87 result, then close.
  const handleDiligenceDone = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
    handleClose(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn("motion-pop w-full bg-paper", widthClass)}>
        {step === "locate" && (
          // One debounced typeahead, replacing the tabbed Address / BIN / CSV
          // Step 1. DialogTitle / DialogDescription stay in the DOM and
          // `sr-only` because Radix anchors the dialog's accessible name to
          // them; the panel draws its own headline and subhead.
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Add a building</DialogTitle>
              <DialogDescription>
                Locate a building by typing its address, BIN, or BBL — we
                pull the rest from NYC city records.
              </DialogDescription>
            </DialogHeader>
            <AddBuildingLocatePanel
              existingBins={existingBins}
              onPick={handleCandidatePick}
              onOpenCsv={() => setStep("csv")}
            />
            {isSubmitting ? (
              <div className="absolute inset-0 grid place-items-center bg-paper/60 backdrop-blur-[1px]">
                <Loader2 className="h-5 w-5 animate-spin text-ink-mute" />
              </div>
            ) : null}
          </>
        )}

        {step === "csv" && (
          <>
            <DialogHeader>
              <DialogTitle>Bulk upload (CSV)</DialogTitle>
              <DialogDescription>
                Drop a CSV with Address and/or NYC_BIN columns. We&apos;ll
                resolve each row in NYC records and stage them for
                confirmation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div
                {...getRootProps()}
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer motion-tint",
                  isDragActive
                    ? "border-teal bg-teal-soft"
                    : "border-ink-line hover:border-ink-mute",
                  uploadStatus === "creating" &&
                    "pointer-events-none opacity-60",
                )}
              >
                <input {...getInputProps()} />
                <Upload className="h-8 w-8 mx-auto mb-2 text-ink-mute" />
                <p className="text-sm text-ink-body">
                  Drop a CSV file here, or click to browse
                </p>
                <p className="text-xs text-ink-mute mt-1">
                  Must have Address and/or NYC_BIN columns
                </p>
              </div>

              {uploadStatus !== "idle" && (
                <div className="flex items-center gap-2 text-sm">
                  {uploadStatus === "parsing" ||
                  uploadStatus === "creating" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-ink-mute" />
                  ) : uploadStatus === "done" ? (
                    <CheckCircle2 className="h-4 w-4 text-teal" />
                  ) : (
                    <XCircle className="h-4 w-4 text-risk-high" />
                  )}
                  <span
                    className={cn(
                      "whitespace-pre-line",
                      uploadStatus === "error"
                        ? "text-risk-high"
                        : "text-ink-body",
                    )}
                  >
                    {uploadMessage}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => setStep("locate")}
                  className="inline-flex items-center gap-1 text-ink-mute hover:text-ink"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to lookup
                </button>
                {uploadStatus === "done" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleClose(false)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {step === "confirm" && resolved && (
          <>
            {/* sr-only a11y title — the VerifyShell draws its own headline and
                provenance UI. */}
            <DialogHeader className="sr-only">
              <DialogTitle>Confirm building details</DialogTitle>
              <DialogDescription>
                We found this building. Check the details before adding.
              </DialogDescription>
            </DialogHeader>

            <AddBuildingVerifyPanel
              buildingName={
                resolved.addressResolved ||
                resolved.addressInput ||
                (resolved.bin ? `BIN ${resolved.bin}` : "Selected building")
              }
              bin={resolved.bin}
              bbl={resolved.bbl}
              borough={resolved.borough}
              // From locate-detail's per-field source map. When the call has
              // not returned or failed, every field falls back to a
              // single-PLUTO pill and the tier collapses to "estimate", with no
              // loading flicker.
              confidence={uiConfidence}
              conflictCount={conflictCount}
              userOverrodeAny={editedFields.size > 0}
              fields={[
                {
                  key: "grossFloorArea",
                  label: "Gross floor area",
                  helper:
                    "Total enclosed area as it would appear on a building permit. Drives LL97 intensity limits.",
                  display: gfaInput
                    ? Number(gfaInput).toLocaleString()
                    : "—",
                  raw: gfaInput,
                  unit: "sq ft",
                  numeric: true,
                  sources: sourcesFor("grossFloorArea", "PLUTO · BldgArea"),
                  conflict: conflictFor("grossFloorArea"),
                },
                {
                  key: "primaryFunction",
                  label: "Primary use",
                  helper:
                    "Use category that best matches the building's main occupancy. Drives the LL97 emissions intensity limit.",
                  display: primaryFunctionInput || "—",
                  raw: primaryFunctionInput,
                  sources: sourcesFor("primaryFunction", "PLUTO · BldgClass"),
                  conflict: conflictFor("primaryFunction"),
                  // DRY with the /portal/buildings/<id>/ inline editor: both
                  // speak the same ENERGY STAR vocabulary, so a value attested
                  // in one reads correctly in the other. One source of truth:
                  // PRIMARY_FUNCTION_OPTIONS.
                  options: PRIMARY_FUNCTION_OPTIONS,
                },
                {
                  key: "yearBuilt",
                  label: "Year built",
                  helper: "Construction year per DOB records.",
                  display: yearBuiltInput || "—",
                  raw: yearBuiltInput,
                  numeric: true,
                  sources: sourcesFor("yearBuilt", "PLUTO · YearBuilt"),
                  conflict: conflictFor("yearBuilt"),
                },
              ]}
              footerNote="Everything here will populate the building's profile and feed the penalty estimate. You can edit any field later from the building page."
              primaryLabel={
                isSubmitting
                  ? "Adding…"
                  : "Looks right — add building"
              }
              isSubmitting={isSubmitting}
              primaryDisabled={cannotAttest}
              primaryDisabledReason={
                cannotAttest
                  ? "City records returned no data for this building. Add it with City estimates instead — you can fill in details later."
                  : undefined
              }
              onPrimary={() => handleSubmitConfirm(true)}
              onCityEstimates={() => handleSubmitConfirm(false)}
              onShareEspm={handleEspmShare}
              onFieldEdit={(key, value) => {
                if (key === "grossFloorArea") setGfaInput(value);
                else if (key === "primaryFunction")
                  setPrimaryFunctionInput(value);
                else if (key === "yearBuilt") setYearBuiltInput(value);
                // Mark the field overridden so the confidence bar and pills
                // stop claiming agreement on a value neither PLUTO nor LL84
                // said.
                setEditedFields((prev) => {
                  if (prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.add(key);
                  return next;
                });
              }}
            />

            {!prefill && (
              <button
                type="button"
                onClick={() => {
                  setStep("locate");
                  setResolved(null);
                  setDetail(null);
                  setEditedFields(new Set());
                  latestPickRef.current = null;
                }}
                disabled={isSubmitting}
                className="absolute left-4 top-4 inline-flex items-center gap-1 text-xs text-ink-mute hover:text-ink disabled:opacity-50"
                aria-label="Back to lookup"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Back
              </button>
            )}
          </>
        )}

        {step === "collecting" && createdBuildingId && (
          <>
            {/* sr-only a11y title — CollectingFrame draws its own headline and
                group rows. */}
            <DialogHeader className="sr-only">
              <DialogTitle>Collecting building records</DialogTitle>
              <DialogDescription>
                Pulling identity, exposure, and inspection records from
                NYC city sources.
              </DialogDescription>
            </DialogHeader>
            <CollectingFrame
              buildingId={createdBuildingId}
              onAdvance={handleCollectingAdvance}
              onCancel={() => handleClose(false)}
            />
          </>
        )}

        {step === "diligence" && createdBuildingId && (
          <>
            {/* sr-only a11y title — the diligence panel draws its own
                headline. */}
            <DialogHeader className="sr-only">
              <DialogTitle>LL87 diligence</DialogTitle>
              <DialogDescription>
                What NYC records show for this building&rsquo;s Energy
                Efficiency Report (Local Law 87) obligation.
              </DialogDescription>
            </DialogHeader>
            <AddBuildingLl87DiligencePanel
              buildingId={createdBuildingId}
              onDone={handleDiligenceDone}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddBuildingDialog;
